import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useRef,
} from "react";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { useLocation, useHistory } from "react-router-dom";
import { UserContext } from "context/user";
import { getFarms, getFields, updateFarm } from "api/Farm";
import Table from "components/Table";
import Button from "components/Button";
import AddFarm from "components/Modal/AddFarm";
import UploadFile from "components/Modal/UploadFile";
import ShareFarm from "components/Modal/ShareFarm/";
import DeleteFarm from "components/Modal/DeleteFarm";
import FarmCheckboxHeader from "./components/FarmCheckboxHeader";
import FarmCheckboxCell from "./components/FarmCheckboxCell";
import FarmNameHeader from "./components/FarmNameHeader";
import FarmNameCell from "./components/FarmNameCell";
import CollaboratorsCell from "./components/CollaboratorsCell";
import FieldCount from "./components/FieldCount";
import TotalAreaCell from "./components/TotalAreaCell";
import UpdatedAtCell from "./components/UpdatedAtCell";
import Fields from "./components/Fields";
import "./index.scss";

const FarmsAndFieldsContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const { state } = location;
  const { token, user, profile } = useContext(UserContext);
  const fetchIdRef = useRef(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState(null);
  const [farms, setFarms] = useState([]);
  const [selectedFarms, setSelectedFarms] = useState([]);
  const [expandedFarm, setExpandedFarm] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [farmName, setFarmName] = useState("");
  const [farmInfo, setFarmInfo] = useState(null);
  const [isOpenShareModal, setIsOpenShareModal] = useState(false);
  const [inviteFarm, setInviteFarm] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [sortValue, setSortValue] = useState("updated_on");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isSorted, setIsSorted] = useState(false);

  const fetchField = useCallback(async () => {
    if (!expandedFarm) return null;

    setFieldsLoading(true);
    const farm = farms[expandedFarm];
    const fieldsData = await getFields(farm.properties.farm_id, token);
    setFields(fieldsData.features);
    setFieldsLoading(false);
    return null;
  }, [farms, expandedFarm, token]);

  useEffect(() => {
    setFields([]);
    fetchField();
  }, [fetchField, expandedFarm]);

  const fetchData = useCallback(
    async ({ pageSize, pageIndex }) => {
      fetchIdRef.current += 1;
      const fetchId = fetchIdRef.current;
      if (fetchId === fetchIdRef.current && token) {
        const limit = pageSize;
        const skip = pageSize * pageIndex;

        setLoading(true);
        // setPageSize(pageSize);
        // setCurrentPage(pageIndex);
        try {
          let query = `skip=${skip}&limit=${limit}`;

          if (sortValue || sortOrder) {
            query += `&sort_by=${sortValue}&sort_dir=${sortOrder}`;
          }

          if (searchTerm) {
            query += `&search_query=${searchTerm}`;
          }

          if (state?.filter === "favourites") {
            query += "&is_favorite=true";
          }
          const farmsData = await getFarms(query, token);

          setFarms(farmsData.features);
          setPageCount(farmsData.pages.total_pages);
        } catch (e) {
          toast.error(e);
        }
        setLoading(false);
      }
    },
    [searchTerm, token, state, sortValue, sortOrder]
  );

  const toggleFarms = useCallback(() => {
    if (farms.length !== selectedFarms.length) {
      const farmIds = farms.map((item) => item.properties.farm_id);
      setSelectedFarms(farmIds);
    } else {
      setSelectedFarms([]);
    }
  }, [farms, selectedFarms]);

  const selectFarm = useCallback(
    (farmId) => {
      if (selectedFarms.includes(farmId)) {
        setSelectedFarms((items) => items.filter((item) => item !== farmId));
      } else {
        setSelectedFarms((items) => [...items, farmId]);
      }
    },
    [selectedFarms]
  );

  const handleSearch = debounce(
    (search) => {
      setSearchTerm(search);
    },
    1500,
    {
      maxWait: 1500,
    }
  );

  const handleChangeStatus = () => {};

  const createNewFarm = () => {
    setIsOpenModal(true);
  };

  const updateFavoriteStatus = useCallback(
    async (farmId) => {
      try {
        const newFarms = [...farms];
        const farm = newFarms.find(
          (item) => item.properties.farm_id === farmId
        );
        const params = {
          data: {
            farm_name: farm.properties.farm_name,
            is_account_farm_favorite: !farm.properties.is_favorite,
          },
        };
        farm.properties.is_favorite = !farm.properties.is_favorite;
        await updateFarm(farmId, params, token);
        setFarms(newFarms);
      } catch (e) {
        toast.error(e);
      }
    },
    [farms, token]
  );

  const closeModal = () => {
    setFarmName("");
    setIsOpenModal(false);
  };

  const handleShareFarmModal = (i) => {
    setIsOpenShareModal(true);
    setInviteFarm(i);
  };

  const handleDeleteFarm = (data) => {
    setFarmInfo(data);
    setIsOpenDeleteModal(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <FarmCheckboxHeader
            {...props}
            isSelectedAllFarms={farms.length === selectedFarms.length}
            toggleFarms={toggleFarms}
          />
        ),
        accessor: "properties.farm_id",
        Cell: (props) => (
          <FarmCheckboxCell
            {...props}
            selectedFarms={selectedFarms}
            selectFarm={selectFarm}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: FarmNameHeader,
        accessor: "properties.farm_name",
        Cell: (props) => (
          <FarmNameCell {...props} setAsFavorite={updateFavoriteStatus} />
        ),
      },
      {
        Header: "Collaborators",
        accessor: "properties.collaborators",
        Cell: (props) => (
          <CollaboratorsCell
            {...props}
            sendInvite={(e) => handleShareFarmModal(e)}
          />
        ),
      },
      {
        Header: "No. of Fields",
        accessor: "properties.number_of_fields",
        Cell: FieldCount,
      },
      {
        Header: "Total Area",
        accessor: "properties.farm_area",
        Cell: TotalAreaCell,
      },
      {
        Header: "Last Updated",
        accessor: "properties.updated_on",
        Cell: (props) => (
          <UpdatedAtCell
            {...props}
            expandedFarm={expandedFarm}
            expandFarm={setExpandedFarm}
            deleteFarm={(e) => handleDeleteFarm(e)}
          />
        ),
      },
    ],
    [
      expandedFarm,
      farms.length,
      selectFarm,
      selectedFarms,
      toggleFarms,
      updateFavoriteStatus,
    ]
  );

  useEffect(() => {
    if (!profile) return;
    if (!profile?.account?.terms_accepted_version && user?.email_verified) {
      history.push("/getting-started/terms-and-conditions");
    } else if (
      !profile?.account?.terms_accepted_version &&
      !user?.email_verified
    ) {
      history.push("/getting-started/profile-setup");
    }
  }, [history, profile, user]);

  const removeFilterByFavourites = () => {
    if (state?.filter) {
      history.replace({
        pathname: "/farms",
        state: {},
      });
    }
  };

  const handleSelectSortOrder = (order) => {
    setSortOrder(order);
    setIsSorted(!isSorted);
  };

  const handleClickedHeader = (e, col) => {
    e.preventDefault();
    const column = col.id.split(".");
    setSortValue(column[1]);
  };

  return (
    <div data-testid="farms-page" className="farms-page">
      <section className="header-section">
        <h1 className="title">Farms & Fields</h1>
        <p className="description">
          Manage your farms by creating a farm, sharing with collaborators or
          save them as your favorites.
        </p>
      </section>

      <div className="actions-wrapper">
        <div className="left">
          <div className="search">
            <span className="icon-search" />
            <input
              data-testid="search-input"
              placeholder="Search for Farms..."
              className="search-input"
              name="searchString"
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="right">
          {state?.filter === "favourites" && (
            <Button
              data-testid="clear-filter"
              type="solid"
              color="white"
              size="normal"
              icon="close"
              label="Remove Filter By Favourites"
              onClick={() => removeFilterByFavourites()}
            />
          )}
          <Button
            color="blue"
            type="solid"
            label="Share Multiple Farms"
            icon="more"
            size="normal"
            block
            onClick={() => setIsOpenShareModal(true)}
            disabled={!(selectedFarms.length > 1)}
          />
          <Button
            data-testid="create-farm"
            type="solid"
            color="green"
            size="normal"
            label="Create Farm"
            icon="add"
            block
            onClick={() => createNewFarm()}
          />
        </div>
      </div>
      <Table
        pageCount={pageCount}
        fetchData={fetchData}
        columns={columns}
        loading={loading}
        data={farms}
        subComponent={<Fields loading={fieldsLoading} fields={fields} />}
        onRowClick={(event, row) => {
          const disabledDivs = [
            "check-box",
            "favorite",
            "button",
            "icon-redirect",
            "icon-trash",
            "collapse",
          ];

          if (!disabledDivs.includes(event.target.classList[0])) {
            history.push(`/farms/${row.values["properties.farm_id"]}`);
          }
        }}
        onHeaderClick={(event, col) => handleClickedHeader(event, col)}
        isSorted={isSorted}
        setSortOrder={handleSelectSortOrder}
        sortValue={sortValue}
      />
      <AddFarm
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        onClose={closeModal}
        setFarmDetails={setFarmName}
        buttonAction={() => setOpenUploadFile(true)}
      />
      <UploadFile
        farmName={farmName}
        show="New"
        handleChangeStatus={() => handleChangeStatus}
        isOpen={openUploadFile}
        onRequestClose={() => setOpenUploadFile(false)}
        onClose={() => setOpenUploadFile(false)}
      />
      <DeleteFarm
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal(false)}
        farmInfo={farmInfo}
        data-testid="delete-farm-modal"
        reload
      />
      <ShareFarm
        isOpen={isOpenShareModal}
        onClose={() => setIsOpenShareModal(false)}
        selectedFarms={selectedFarms}
        farm={inviteFarm}
        view="farms"
      />
    </div>
  );
};

export default FarmsAndFieldsContainer;
